import type { ChangeEventHandler, ReactNode } from 'react';
import type { NewMediaObject } from '../../lib/store/newMediaStore';
import type { ButtonProps } from '../Button/Button';

import { use, useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getTestProps } from '../../lib/helpers';
import { RolePermissions } from '../../lib/rolePermissions';

import UserContext from '../../contexts/UserContext';

import Button from '../Button/Button';
import Loader from '../Loader/Loader';

export type FileButtonUploadFn = (
  files: null | FileList | File[],
) => Promise<void | NewMediaObject[]>;

interface FileButtonProps extends ButtonProps {
  /**
   * On upload callback
   */
  onUpload?: FileButtonUploadFn;
  /**
   * If file selection is multiple
   */
  multiple?: boolean;
  /**
   * If button is disabled
   */
  disabled?: boolean;
  /**
   * On blur callback
   */
  onBlur?: () => void;
  /**
   * Additional button classes
   */
  additionalClasses?: string;
  /**
   * Accepted mimetypes
   */
  accept?: string;
  /**
   * File button children
   */
  children?: ReactNode;
  /**
   * Button test id
   */
  testId?: string;
}

const FileButton = ({
  onUpload,
  multiple = false,
  disabled = false,
  onBlur,
  buttonSize = 'base',
  additionalClasses = '',
  accept = '',
  buttonColor,
  children,
  testId = '',
}: FileButtonProps) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);

  const { permissions } = use(UserContext);

  const canCreate = permissions.canCo(
    '_media',
    RolePermissions.PERMISSIONS_TYPES.CREATE,
  );

  const handleUpload = useCallback<ChangeEventHandler<HTMLInputElement>>(
    async (e) => {
      setIsUploading(true);
      await onUpload?.(e.target.files);
      if (inputRef.current?.value) inputRef.current.value = '';
      setIsUploading(false);
    },
    [onUpload],
  );

  return (
    <Button
      as="label"
      buttonSize={buttonSize}
      onBlur={onBlur}
      iconImage={
        isUploading ? <Loader type="spinner-grid" size="tiny" /> : null
      }
      disabled={isUploading || disabled || !canCreate}
      additionalClasses={additionalClasses}
      buttonColor={buttonColor}
      {...getTestProps(testId, 'add-media', 'testId')}
    >
      <>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          onChange={handleUpload}
          multiple={multiple}
          disabled={isUploading || disabled}
          {...(accept && { accept })}
          {...getTestProps(testId, 'file-input')}
        />
        {children || t('Media.AddFile')}
      </>
    </Button>
  );
};

export default FileButton;
